import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createVNode as _createVNode, createElementVNode as _createElementVNode, createBlock as _createBlock, unref as _unref, resolveDynamicComponent as _resolveDynamicComponent, isRef as _isRef, withCtx as _withCtx, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, normalizeClass as _normalizeClass, renderList as _renderList, Fragment as _Fragment, toDisplayString as _toDisplayString } from "vue"

const _hoisted_1 = { class: "designer-container" }
const _hoisted_2 = {
  key: 0,
  class: "no-path"
}
const _hoisted_3 = {
  key: 1,
  class: "path-heading sub-heading sub-heading-right"
}
const _hoisted_4 = { class: "title is-6" }
const _hoisted_5 = {
  key: 0,
  class: "delete-path-button"
}
const _hoisted_6 = {
  key: 2,
  class: "path-contents"
}
const _hoisted_7 = { class: "state-menu" }
const _hoisted_8 = ["onClick"]
const _hoisted_9 = { class: "cube-icon" }
const _hoisted_10 = {
  key: 1,
  class: "state-list ending-states"
}
const _hoisted_11 = { class: "state-menu" }
const _hoisted_12 = ["onClick"]
const _hoisted_13 = { class: "modal-card-title" }
const _hoisted_14 = { key: 0 }
const _hoisted_15 = {
  key: 1,
  id: "linked-path-message"
}
const _hoisted_16 = { key: 2 }

import { ref, computed, watch, nextTick } from 'vue';
import { useStore } from 'vuex';
import Draggable from 'vuedraggable';
import { nanoid } from 'nanoid';
import { StateViewModel, PathViewModel } from '@/features/bots/models/designer';
import EditableText from '@/shared/components/EditableText.vue';
import ConfirmationModal from '@/shared/components/ConfirmationModal.vue';
import Modal from '@/shared/components/Modal.vue';
import TextComponent from './TextComponent.vue';
import InputStringComponent from './InputStringComponent.vue';
import QuickReplyComponent from './QuickReplyComponent.vue';
import HumanHandoverComponent from './HumanHandoverComponent.vue';
import AppIntegrationComponent from './AppIntegrationComponent.vue';
import DecisionComponent from './DecisionComponent.vue';
import GoToComponent from './GoToComponent.vue';
import DropDownMenu from '@/shared/components/dropDownMenu/DropDownMenu.vue';
import TriggerBox from './TriggerBox.vue';
import LinkedPathsBox from './LinkedPathsBox.vue';
import QuestionIcon from 'vue-material-design-icons/ForumOutline.vue';
import QuickReplyIcon from 'vue-material-design-icons/PlaylistCheck.vue';
import DecisionIcon from 'vue-material-design-icons/SourceBranch.vue';
import PauseBotIcon from 'vue-material-design-icons/Pause.vue';
import TextIcon from 'vue-material-design-icons/FormatTitle.vue';
import DeleteIcon from 'vue-material-design-icons/TrashCanOutline.vue';
import GoToIcon from '@/shared/icons/GoToIcon.vue';
import { DELETE_PATH } from '@/store/bot-designer/paths/actionTypes';
import { ADD_STATE, UPDATE_STATE } from '@/store/bot-designer/states/mutationTypes';
import { DELETE_STATE } from '@/store/bot-designer/states/actionTypes';
import { UPDATE_PATH } from '@/store/bot-designer/paths/mutationTypes';
import { ConditionViewModel } from '@shared/components/conditions/models/conditionViewModel';
import { usePath } from '@/features/bots/models/designer/usePath';

enum DeleteModalType {
  State,
  Path,
}


export default /*@__PURE__*/_defineComponent({
  __name: 'PathDesigner',
  setup(__props) {

const COMPONENTS = {
  TextComponent,
  InputStringComponent,
  QuickReplyComponent,
  HumanHandoverComponent,
  AppIntegrationComponent,
  DecisionComponent,
  GoToComponent,
};

const store = useStore();
const currentPathId = computed<string>(() => store.getters.currentPathId);
const currentPath = computed<PathViewModel>(() => store.getters.currentPath);
const firstPath = computed(() => store.getters.firstPath);
const linkedPaths = computed(() => store.getters.linkedPaths);
const trigger = computed(() => store.getters.trigger);
const triggerConditions = computed(() => store.getters.triggerConditions);
const audienceConditions = computed(() => store.getters.audienceConditions);
const scheduleCondition = computed<ConditionViewModel | undefined>(() => store.getters.scheduleCondition);
const openingTimes = computed(() => store.getters.openingTimes);

const { reorderableStates, pathName, hasEndingStates, endStates } = usePath(currentPath);

const isStatePickerUp = ref(false);

const deleteModalOptions = ref({
  open: false,
  type: DeleteModalType.State,
  title: '',
  confirmation: '',
  confirm: () => {},
  cancel: () => {},
});

const stateRefs = ref<{ [key: string]: any }>({});

function setStateRef(el: any, id: string) {
  if (el) {
    stateRefs.value[id] = el;
  }
}

const openDeletePathModal = () => {
  deleteModalOptions.value = {
    open: true,
    type: DeleteModalType.Path,
    title: 'Delete Path',
    confirmation: '',
    confirm: deletePathConfirmed,
    cancel: closeDeleteModal,
  };
};

const openDeleteStateModal = (state: StateViewModel) => {
  deleteModalOptions.value = {
    open: true,
    type: DeleteModalType.State,
    title: 'Delete State',
    confirmation: 'Are you sure you want to delete the state?',
    confirm: () => deleteStateConfirmed(state),
    cancel: closeDeleteModal,
  };
};

const closeDeleteModal = () => {
  deleteModalOptions.value.open = false;
};

const deletePathConfirmed = () => {
  store.dispatch(DELETE_PATH, currentPath.value.id);
  closeDeleteModal();
};

const deleteStateConfirmed = (state: StateViewModel) => {
  store.dispatch(DELETE_STATE, state);
  closeDeleteModal();
};

const getStateComponent = (state: StateViewModel) => {
  if (state.component === 'InputString' && state.options?.length) {
    return COMPONENTS.QuickReplyComponent;
  }
  const name = `${state.component}Component`;
  return (COMPONENTS as any)[name] || name;
};

const isFirstPathInFlow = computed(() => currentPath.value?.id === firstPath.value?.id);

const addState = (component: string, additionalProps = {}) => {
  store.commit(ADD_STATE, {
    id: `state-${nanoid(7)}`,
    component,
    ...additionalProps,
  });
  focusOnStateInput();
};

const addMessageState = () => addState('Text', { text: '', invalid: true });
const addQuestionState = () => addState('InputString', { text: '', invalid: true });
const addDecisionState = () =>
  addState('Decision', {
    invalid: true,
    decisions: [{ transition: {}, conditions: { type: 'and', conditions: [] } }],
    noMatch: {},
  });
const addAppIntegrationState = () => addState('AppIntegration');
const addQuickReplyState = () =>
  addState('InputString', { text: '', allowedAttempts: 1, options: [{ id: '1', text: '' }], invalid: true });
const addHumanHandoverState = () => addState('HumanHandover');
const addGoToState = () => addState('GoTo', { transition: {}, invalid: true });

const stateUpdated = (state: StateViewModel) => {
  store.commit(UPDATE_STATE, state);
};

const pathNameUpdated = (text: string) => {
  currentPath.value.name = text;
  store.commit(UPDATE_PATH, currentPath.value);
};

const setIsStatePickerUp = (val: boolean) => {
  isStatePickerUp.value = val;
};

const focusOnStateInput = async () => {
  await nextTick();
  const lastState = getLastAddedState();
  if (lastState && typeof lastState.focusOnInput === 'function') {
    lastState.focusOnInput();
  }
};

const getLastAddedState = () => {
  const states = currentPath.value.states;
  if (!states.length) return null;
  const lastStateId = states[states.length - 1].id;
  return stateRefs.value[lastStateId] || null;
};

watch(
  currentPath,
  (newValue: PathViewModel, oldValue: PathViewModel) => {
    if (oldValue && newValue.id !== oldValue.id) {
      store.commit(UPDATE_PATH, newValue);
    }
  },
  { deep: true, immediate: true },
);

return (_ctx: any,_cache: any) => {
  const _component_font_awesome_icon = _resolveComponent("font-awesome-icon")!
  const _component_n_button = _resolveComponent("n-button")!
  const _component_n_button_group = _resolveComponent("n-button-group")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    (!currentPath.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_2, "No path selected"))
      : _createCommentVNode("", true),
    (currentPath.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
          _createElementVNode("h5", _hoisted_4, [
            _createVNode(EditableText, {
              value: currentPath.value.name,
              onTextUpdated: pathNameUpdated,
              minWidth: "200px",
              maxWidth: "800px"
            }, null, 8, ["value"])
          ]),
          (!isFirstPathInFlow.value)
            ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
                _createElementVNode("a", {
                  onClick: _cache[0] || (_cache[0] = ($event: any) => (openDeletePathModal()))
                }, [
                  _createVNode(DeleteIcon, { class: "icon-1-3x" })
                ])
              ]))
            : _createCommentVNode("", true)
        ]))
      : _createCommentVNode("", true),
    (currentPath.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
          (isFirstPathInFlow.value)
            ? (_openBlock(), _createBlock(TriggerBox, {
                key: 0,
                value: trigger.value,
                triggerConditions: triggerConditions.value,
                audienceConditions: audienceConditions.value,
                schedule: scheduleCondition.value,
                openingTimes: openingTimes.value
              }, null, 8, ["value", "triggerConditions", "audienceConditions", "schedule", "openingTimes"]))
            : _createCommentVNode("", true),
          _createVNode(LinkedPathsBox),
          _createVNode(_unref(Draggable), {
            modelValue: _unref(reorderableStates),
            "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => (_isRef(reorderableStates) ? (reorderableStates).value = $event : null)),
            class: "state-list",
            handle: ".island-badge",
            "item-key": "id"
          }, {
            item: _withCtx(({ element: state }) => [
              (_openBlock(), _createElementBlock("div", {
                class: "state",
                key: state.id
              }, [
                (_openBlock(), _createBlock(_resolveDynamicComponent(getStateComponent(state)), {
                  ref: (el) => setStateRef(el, state.id),
                  value: state,
                  onStateUpdated: stateUpdated
                }, null, 40, ["value"])),
                _createElementVNode("div", _hoisted_7, [
                  _createElementVNode("a", {
                    id: "delete-state",
                    class: "delete is-small delete-state",
                    onClick: ($event: any) => (openDeleteStateModal(state))
                  }, null, 8, _hoisted_8)
                ])
              ]))
            ]),
            _: 1
          }, 8, ["modelValue"]),
          _createVNode(DropDownMenu, {
            class: _normalizeClass(["add-state-container", { 'is-up': isStatePickerUp.value }]),
            dropDownIcon: "big-add-circle",
            onIsUp: setIsStatePickerUp
          }, {
            default: _withCtx(() => [
              _createElementVNode("a", {
                onClick: _cache[2] || (_cache[2] = ($event: any) => (addMessageState())),
                class: "dropdown-item"
              }, [
                _createVNode(TextIcon),
                _cache[14] || (_cache[14] = _createTextVNode(" Message "))
              ]),
              _createElementVNode("a", {
                onClick: _cache[3] || (_cache[3] = ($event: any) => (addQuestionState())),
                class: "dropdown-item"
              }, [
                _createVNode(QuestionIcon),
                _cache[15] || (_cache[15] = _createTextVNode(" Question "))
              ]),
              _createElementVNode("a", {
                onClick: _cache[4] || (_cache[4] = ($event: any) => (addDecisionState())),
                class: "dropdown-item"
              }, [
                _createVNode(DecisionIcon),
                _cache[16] || (_cache[16] = _createTextVNode(" Decision "))
              ]),
              _createElementVNode("a", {
                onClick: _cache[5] || (_cache[5] = ($event: any) => (addQuickReplyState())),
                class: "dropdown-item"
              }, [
                _createVNode(QuickReplyIcon),
                _cache[17] || (_cache[17] = _createTextVNode(" Quick Replies "))
              ]),
              _createElementVNode("a", {
                onClick: _cache[6] || (_cache[6] = ($event: any) => (addAppIntegrationState())),
                class: "dropdown-item"
              }, [
                _createElementVNode("span", _hoisted_9, [
                  _createVNode(_component_font_awesome_icon, { icon: "cube" })
                ]),
                _cache[18] || (_cache[18] = _createTextVNode(" App Action "))
              ]),
              (!_unref(hasEndingStates))
                ? (_openBlock(), _createElementBlock("a", {
                    key: 0,
                    onClick: _cache[7] || (_cache[7] = ($event: any) => (addGoToState())),
                    class: "dropdown-item"
                  }, [
                    _createVNode(GoToIcon),
                    _cache[19] || (_cache[19] = _createTextVNode(" Go To "))
                  ]))
                : _createCommentVNode("", true),
              (!_unref(hasEndingStates))
                ? (_openBlock(), _createElementBlock("a", {
                    key: 1,
                    onClick: _cache[8] || (_cache[8] = ($event: any) => (addHumanHandoverState())),
                    class: "dropdown-item"
                  }, [
                    _createVNode(PauseBotIcon),
                    _cache[20] || (_cache[20] = _createTextVNode(" Pause Bot "))
                  ]))
                : _createCommentVNode("", true)
            ]),
            _: 1
          }, 8, ["class"]),
          (_unref(hasEndingStates))
            ? (_openBlock(), _createElementBlock("div", _hoisted_10, [
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(endStates), (state) => {
                  return (_openBlock(), _createElementBlock("div", {
                    class: "state",
                    key: state.id
                  }, [
                    (_openBlock(), _createBlock(_resolveDynamicComponent(getStateComponent(state)), {
                      value: state,
                      onStateUpdated: stateUpdated
                    }, null, 40, ["value"])),
                    _createElementVNode("div", _hoisted_11, [
                      _createElementVNode("a", {
                        class: "delete is-small",
                        onClick: ($event: any) => (openDeleteStateModal(state))
                      }, null, 8, _hoisted_12)
                    ])
                  ]))
                }), 128))
              ]))
            : _createCommentVNode("", true)
        ]))
      : _createCommentVNode("", true),
    (deleteModalOptions.value.open && deleteModalOptions.value.type === DeleteModalType.Path)
      ? (_openBlock(), _createBlock(Modal, {
          key: 3,
          id: "delete-path-modal",
          onCancel: _cache[11] || (_cache[11] = ($event: any) => (deleteModalOptions.value.cancel()))
        }, {
          title: _withCtx(() => [
            _createElementVNode("p", _hoisted_13, _toDisplayString(deleteModalOptions.value.title), 1)
          ]),
          body: _withCtx(() => [
            (linkedPaths.value.length > 0)
              ? (_openBlock(), _createElementBlock("p", _hoisted_14, "This path is linked to by the following path(s):"))
              : _createCommentVNode("", true),
            _createElementVNode("ul", null, [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(linkedPaths.value, (linkedPath) => {
                return (_openBlock(), _createElementBlock("div", {
                  key: linkedPath.id
                }, [
                  _createElementVNode("li", null, _toDisplayString(linkedPath.name), 1)
                ]))
              }), 128))
            ]),
            (linkedPaths.value.length > 0)
              ? (_openBlock(), _createElementBlock("p", _hoisted_15, " These paths will become invalid if you delete " + _toDisplayString(_unref(pathName)) + ". Are you sure you want to continue? ", 1))
              : _createCommentVNode("", true),
            (linkedPaths.value.length === 0)
              ? (_openBlock(), _createElementBlock("p", _hoisted_16, "Are you sure you want to delete " + _toDisplayString(_unref(pathName)) + "?", 1))
              : _createCommentVNode("", true)
          ]),
          buttons: _withCtx(() => [
            _createVNode(_component_n_button_group, null, {
              default: _withCtx(() => [
                _createVNode(_component_n_button, {
                  variant: "primary",
                  onClick: _cache[9] || (_cache[9] = ($event: any) => (deleteModalOptions.value.confirm()))
                }, {
                  default: _withCtx(() => _cache[21] || (_cache[21] = [
                    _createTextVNode("Delete")
                  ])),
                  _: 1
                }),
                _createVNode(_component_n_button, {
                  onClick: _cache[10] || (_cache[10] = ($event: any) => (deleteModalOptions.value.cancel())),
                  variant: "primary--outline"
                }, {
                  default: _withCtx(() => _cache[22] || (_cache[22] = [
                    _createTextVNode("Cancel")
                  ])),
                  _: 1
                })
              ]),
              _: 1
            })
          ]),
          _: 1
        }))
      : _createCommentVNode("", true),
    (deleteModalOptions.value.open && deleteModalOptions.value.type === DeleteModalType.State)
      ? (_openBlock(), _createBlock(ConfirmationModal, {
          key: 4,
          id: "delete-modal",
          confirmationTitle: deleteModalOptions.value.title,
          confirmationMessage: deleteModalOptions.value.confirmation,
          onConfirm: _cache[12] || (_cache[12] = ($event: any) => (deleteModalOptions.value.confirm())),
          onCancel: _cache[13] || (_cache[13] = ($event: any) => (deleteModalOptions.value.cancel()))
        }, null, 8, ["confirmationTitle", "confirmationMessage"]))
      : _createCommentVNode("", true)
  ]))
}
}

})